import { Component, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ChdTimeInputPresenter } from '@cp-produkte/cruise-hospitality-design';

import { ComponentsModule } from '../components.module';
import { TimeInput } from '../time-input/time-input.component';

/**
 * App-local dx renderer that draws `ChdTimeInput` with the legacy `spa-time-input`.
 * Plugged into the chd time-input slot via `provideChdDxBackend({ timeInput: ChdTimeInputSpaRenderer })`.
 *
 * `spa-time-input` models time as `{ hour, minute }`; this renderer maps it to the
 * chd public `HH:mm` string. Feature gaps vs. Material: no timepicker dropdown,
 * clear button, or min/max; `disabled` is approximated as `readonly`.
 */
@Component({
  selector: 'spa-chd-time-input-renderer',
  imports: [ComponentsModule, FormsModule],
  templateUrl: './chd-time-input-spa-renderer.component.html',
})
export class ChdTimeInputSpaRenderer {
  protected readonly presenter = inject(ChdTimeInputPresenter);

  // True while the user is mid-edit, so presenter syncs don't overwrite the in-progress draft.
  private readonly editing = signal(false);
  // Local, unpadded draft shown in the field; only committed (padded) once complete.
  protected readonly model = signal<TimeInput | null>(null);

  constructor() {
    effect(() => {
      const value = this.presenter.viewState().value;
      // While editing, ignore presenter pushes: re-deriving from the padded `HH:mm` would reformat
      // a half-typed field (e.g. minute "4" -> "04"), moving the caret and corrupting further input.
      if (!this.editing()) {
        this.model.set(this.toTimeInput(value));
      }
    });
  }

  protected onModelChange(time: TimeInput | null): void {
    this.editing.set(true);
    this.model.set(time);
    // Don't pad/commit a partial value; report empty until both fields are present.
    if (!time?.hour || !time?.minute) {
      this.presenter.onValueChange('');
      return;
    }
    this.presenter.onValueChange(this.toTimeString(time));
  }

  protected onBlur(): void {
    const time = this.model();
    if (time?.hour && time.minute) {
      this.presenter.onValueChange(this.toTimeString(time));
    }
    this.editing.set(false);
    this.presenter.onBlur(new Event('blur'));
  }

  private toTimeInput(value: string): TimeInput | null {
    if (!value) {
      return null;
    }
    const [hour, minute] = value.split(':');
    return { hour, minute };
  }

  private toTimeString(time: TimeInput): string {
    return `${time.hour.padStart(2, '0')}:${time.minute.padStart(2, '0')}`;
  }
}
